import { Scenario, ScenarioSummary, ScheduleResult, ComparisonResult } from '../domain/types.js';
import { buildSchedule, ScheduleError } from '../domain/schedule.js';
import { summarize } from '../domain/metrics.js';
import { validateScenario, ValidationIssue } from '../domain/validators.js';
import { compare } from '../domain/comparison.js';
import {
  saveScenario,
  loadScenarios,
  deleteScenario,
  savePreferences,
  loadPreferences,
  isScenarioShape,
} from '../features/persistence/storage.js';

/**
 * Outcome of the last debounced write: `pending` while a save timer runs, `saved` /
 * `failed` from the real `saveScenario` result, `idle` before anything was scheduled.
 */
export type SaveStatus = 'idle' | 'pending' | 'saved' | 'failed';

export type StoreState = {
  scenarios: Scenario[];
  summaries: Record<string, ScenarioSummary>;
  schedules: Record<string, ScheduleResult>;
  lastViewedId?: string;
  baselineId?: string;
  /** Scenarios the user added to the comparison («+ В сравнение»), insertion order, at most MAX_COMPARISON. */
  comparisonIds: string[];
  loaded: boolean;
  /** Ids of stored scenarios that load() could not accept, with the reasons. */
  loadIssues: Record<string, string[]>;
  saveStatus: SaveStatus;
};

/** Upper bound of `comparisonIds`; the scenario in the form and the baseline are shown in addition. */
export const MAX_COMPARISON = 5;

type Listener = (state: StoreState) => void;

type PendingSave = {
  timer: ReturnType<typeof setTimeout>;
  scenario: Scenario;
};

type ComputedScenario = {
  scenario: Scenario;
  schedule: ScheduleResult;
  summary: ScenarioSummary;
};

export const DEBOUNCE_MS = 1500; // 1.5 seconds

const state: StoreState = createEmptyState();
const listeners: Set<Listener> = new Set();
// One debounce timer per scenario so editing A and then B keeps both saves.
const pendingSaves = new Map<string, PendingSave>();
// Set by any failed write; cleared when the next batch of saves starts. The status settles
// to `saved` only when every write of the batch succeeded, so a later successful write of
// scenario B cannot mask the failed write of scenario A.
let writeFailed = false;
// Scenarios upserted without persisting (the built-in example on a fresh visit). They are
// written as soon as the user acts on them: edits, makes one the baseline or adds another.
const unsavedIds = new Set<string>();
let unloadHandlersRegistered = false;

function createEmptyState(): StoreState {
  return {
    scenarios: [],
    summaries: {},
    schedules: {},
    lastViewedId: undefined,
    baselineId: undefined,
    comparisonIds: [],
    loaded: false,
    loadIssues: {},
    saveStatus: 'idle',
  };
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach(listener => listener(state));
}

export function getState(): StoreState {
  return state;
}

function hasScenario(id: string): boolean {
  return state.scenarios.some(s => s.id === id);
}

function persistPreferences() {
  savePreferences({
    locale: 'ru',
    lastViewedId: state.lastViewedId,
    baselineId: state.baselineId,
    comparisonIds: state.comparisonIds,
  });
}

/**
 * Validate and schedule a scenario without touching the state. The stored
 * scenario is a deep copy so later mutations by the caller cannot desync the
 * cached schedule from the scenario it was computed for.
 */
type ScenarioErrors = { errors: string[]; issues: ValidationIssue[] };

function toErrors(issues: ValidationIssue[]): ScenarioErrors {
  return { errors: issues.map(i => `${i.field}: ${i.message}`), issues };
}

function computeScenario(input: Scenario): ComputedScenario | ScenarioErrors {
  // The business-rule validator assumes the shape; a structurally broken entry (e.g. a
  // hand-edited localStorage payload) is reported instead of throwing on a missing array.
  if (!isScenarioShape(input)) {
    return toErrors([{ field: 'scenario', code: 'shape', message: 'Scenario is missing required fields' }]);
  }
  const issues = validateScenario(input);
  if (issues.length > 0) {
    return toErrors(issues);
  }
  const scenario = structuredClone(input);
  try {
    const schedule = buildSchedule(scenario);
    const summary = summarize(scenario, schedule.months);
    return { scenario, schedule, summary };
  } catch (error) {
    if (error instanceof ScheduleError) {
      return toErrors([{ field: 'schedule', code: 'schedule', message: error.message }]);
    }
    throw error;
  }
}

export function load() {
  const scenarios = loadScenarios();
  const prefs = loadPreferences();

  const { rejected } = hydrateScenarios(scenarios, false); // Don't trigger save during load
  state.loadIssues = rejected;
  const rejectedIds = Object.keys(rejected);
  if (rejectedIds.length > 0) {
    console.warn(
      `[store] ${rejectedIds.length} stored scenario(s) could not be loaded: ${rejectedIds.join(', ')}`,
      rejected,
    );
  }

  if (prefs.lastViewedId && hasScenario(prefs.lastViewedId)) {
    state.lastViewedId = prefs.lastViewedId;
  }

  if (prefs.baselineId && hasScenario(prefs.baselineId)) {
    state.baselineId = prefs.baselineId;
  }

  state.comparisonIds = Array.from(new Set(prefs.comparisonIds ?? []))
    .filter(hasScenario)
    .slice(0, MAX_COMPARISON);

  state.loaded = true;
  registerUnloadHandlers();
  notify();
}

export function upsertScenario(next: Scenario, persist: boolean = true): ScenarioErrors {
  const computed = computeScenario(next);
  if ('errors' in computed) {
    return computed;
  }
  const { scenario, schedule, summary } = computed;

  const existingIndex = state.scenarios.findIndex(s => s.id === scenario.id);
  if (existingIndex >= 0) {
    state.scenarios[existingIndex] = scenario;
  } else {
    state.scenarios.push(scenario);
  }
  state.summaries[scenario.id] = summary;
  state.schedules[scenario.id] = schedule;
  state.lastViewedId = scenario.id;

  if (persist) {
    unsavedIds.delete(scenario.id);
    scheduleSave(scenario);
    persistUnsaved();
    persistPreferences();
  } else {
    unsavedIds.add(scenario.id);
  }

  notify();
  return { errors: [], issues: [] };
}

/** Schedules a save for every scenario that was upserted without persisting. */
function persistUnsaved() {
  unsavedIds.forEach(id => {
    const scenario = state.scenarios.find(s => s.id === id);
    if (scenario) scheduleSave(scenario);
  });
  unsavedIds.clear();
}

export function removeScenario(id: string) {
  cancelPendingSave(id);
  unsavedIds.delete(id);

  state.scenarios = state.scenarios.filter(s => s.id !== id);
  delete state.summaries[id];
  delete state.schedules[id];

  if (state.baselineId === id) {
    state.baselineId = undefined;
  }
  state.comparisonIds = state.comparisonIds.filter(other => other !== id);

  const written = deleteScenario(id);
  if (!written) writeFailed = true;
  // The cancelled save may have been the only pending one; the delete is the real write now.
  if (pendingSaves.size === 0 && state.saveStatus !== 'idle') state.saveStatus = writeFailed ? 'failed' : 'saved';

  if (state.lastViewedId === id) {
    state.lastViewedId = state.scenarios.at(-1)?.id;
  }

  persistPreferences();
  notify();
}

export function selectScenario(id: string) {
  if (hasScenario(id)) {
    state.lastViewedId = id;
    persistPreferences();
    notify();
  }
}

export function setBaseline(id: string | null) {
  if (id && !hasScenario(id)) {
    return;
  }
  state.baselineId = id || undefined;
  // A baseline must survive a reload, so an example that was never written is saved now.
  if (id) persistUnsaved();
  persistPreferences();
  notify();
}

/**
 * Adds `id` to the comparison or removes it. Unknown ids and additions beyond
 * MAX_COMPARISON are ignored. A compared scenario must survive a reload, so an
 * example that was never written is saved when it is added.
 */
export function toggleComparison(id: string) {
  if (!hasScenario(id)) return;
  if (state.comparisonIds.includes(id)) {
    state.comparisonIds = state.comparisonIds.filter(other => other !== id);
  } else {
    if (state.comparisonIds.length >= MAX_COMPARISON) return;
    state.comparisonIds = [...state.comparisonIds, id];
    persistUnsaved();
  }
  persistPreferences();
  notify();
}

export function getSchedule(id: string): ScheduleResult | undefined {
  return state.schedules[id];
}

export function listSummaries(): ScenarioSummary[] {
  return state.scenarios
    .map(s => state.summaries[s.id])
    .filter((summary): summary is ScenarioSummary => Boolean(summary));
}

export function compareScenarios(baseId: string, otherId: string): ComparisonResult | undefined {
  const baseSummary = state.summaries[baseId];
  const otherSummary = state.summaries[otherId];
  if (!baseSummary || !otherSummary) {
    return undefined;
  }
  return compare(baseSummary, otherSummary);
}

/**
 * Replace every scenario with the given list. Invalid entries are reported in
 * `rejected` and skipped; the new state is built aside and swapped in as one
 * step, so listeners never observe a half-populated store. With duplicate ids
 * the last entry wins. Pending saves are cancelled because they belong to the
 * replaced state.
 */
export function hydrateScenarios(list: Scenario[], persist: boolean = false): { rejected: Record<string, string[]> } {
  const rejected: Record<string, string[]> = {};
  const scenarios: Scenario[] = [];
  const summaries: Record<string, ScenarioSummary> = {};
  const schedules: Record<string, ScheduleResult> = {};

  list.forEach((item, index) => {
    const computed = computeScenario(item);
    if ('errors' in computed) {
      const id = typeof item?.id === 'string' ? item.id : `#${index}`;
      rejected[id] = computed.errors;
      return;
    }
    const existingIndex = scenarios.findIndex(s => s.id === computed.scenario.id);
    if (existingIndex >= 0) {
      scenarios[existingIndex] = computed.scenario;
    } else {
      scenarios.push(computed.scenario);
    }
    summaries[computed.scenario.id] = computed.summary;
    schedules[computed.scenario.id] = computed.schedule;
  });

  cancelAllPendingSaves();
  unsavedIds.clear();
  state.scenarios = scenarios;
  state.summaries = summaries;
  state.schedules = schedules;
  state.lastViewedId = scenarios.at(-1)?.id;
  state.baselineId = undefined;
  state.comparisonIds = [];

  if (persist) {
    scenarios.forEach(scheduleSave);
  }

  notify();
  return { rejected };
}

/** Write every debounced save now. Called on pagehide / visibilitychange and before tests tear down. */
export function flushPendingSaves() {
  const pending = [...pendingSaves.values()];
  cancelAllPendingSaves();
  if (pending.length === 0) return;
  if (!pending.map(entry => saveScenario(entry.scenario)).every(Boolean)) writeFailed = true;
  setSaveStatus(writeFailed ? 'failed' : 'saved');
}

function setSaveStatus(status: SaveStatus) {
  if (state.saveStatus === status) return;
  state.saveStatus = status;
  notify();
}

/** Test hook: drop state, listeners, pending saves and unload handlers. */
export function reset() {
  cancelAllPendingSaves();
  listeners.clear();
  unregisterUnloadHandlers();
  writeFailed = false;
  unsavedIds.clear();
  Object.assign(state, createEmptyState());
}

function scheduleSave(scenario: Scenario) {
  // A fresh batch (nothing pending) gets a clean slate: the user edited again.
  if (pendingSaves.size === 0) writeFailed = false;
  cancelPendingSave(scenario.id);
  const timer = setTimeout(() => {
    pendingSaves.delete(scenario.id);
    if (!saveScenario(scenario)) writeFailed = true;
    // Another scenario may still be waiting; the status only settles once every timer fired.
    if (pendingSaves.size === 0) setSaveStatus(writeFailed ? 'failed' : 'saved');
    else if (writeFailed) setSaveStatus('failed');
  }, DEBOUNCE_MS);
  pendingSaves.set(scenario.id, { timer, scenario });
  // Set directly: upsertScenario notifies right after scheduling, so no extra notify here.
  state.saveStatus = 'pending';
}

function cancelPendingSave(id: string) {
  const pending = pendingSaves.get(id);
  if (pending) {
    clearTimeout(pending.timer);
    pendingSaves.delete(id);
  }
}

function cancelAllPendingSaves() {
  pendingSaves.forEach(entry => clearTimeout(entry.timer));
  pendingSaves.clear();
}

function onPageHide() {
  flushPendingSaves();
}

function onVisibilityChange() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    flushPendingSaves();
  }
}

function registerUnloadHandlers() {
  if (unloadHandlersRegistered || typeof window === 'undefined') return;
  unloadHandlersRegistered = true;
  window.addEventListener('pagehide', onPageHide);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
}

function unregisterUnloadHandlers() {
  if (!unloadHandlersRegistered) return;
  unloadHandlersRegistered = false;
  if (typeof window !== 'undefined') {
    window.removeEventListener('pagehide', onPageHide);
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
}
